// Room variables on the client (contract section 7): the current values, the defaults, local edits
// (sent through `send`, which the net client coalesces to ~10 Hz) and incoming changes from the server.
//
// While the user drags a control, the server's broadcasts carry our own earlier values (they come back
// 100-200 ms later); applying them would make the slider jump back. So a key changed locally in the last
// HOLD_MS ignores incoming values; they are remembered, and when the hold ends the key takes the server's
// last value if it differs (someone else wrote after us: last write wins on the server, and every client
// converges to it).
//
// Local mode (`local = true`, docs/plans/admin.md decision 1): the server locks the lights and this browser is not in
// edit mode. Edits apply here only: nothing is sent and no hold is kept, so the change stays until the server sends
// a new value for that key (an editor's change) or the page reloads.

import { DEFAULT_ROOM_VARS, ROOM_VAR_KEYS, type RoomVarKey, type RoomVars, type RoomVarsPatch } from '@lr3d/shared';

const HOLD_MS = 600;

export class RoomVarsStore {
  readonly values: RoomVars;
  readonly defaults: RoomVars;
  /** Last value seen from the server per key (welcome or vars broadcast). */
  private server: RoomVarsPatch = {};
  private heldUntil = new Map<RoomVarKey, number>();
  private holdTimer = 0;
  /** Called after any change (local or remote) with the full current values. */
  onChange: (vars: RoomVars, local: boolean) => void = () => {};
  /** Sends a local change (set by the app once the net client exists). */
  send: (patch: RoomVarsPatch) => void = () => {};
  /** Edits stay in this browser (lights locked, not an editor). */
  local = false;

  constructor() {
    this.defaults = { ...DEFAULT_ROOM_VARS };
    this.values = { ...this.defaults };
  }

  /** A local edit (panel). Applied at once and sent. */
  set(patch: RoomVarsPatch): void {
    if (this.local) {
      const v = this.values as unknown as Record<string, unknown>;
      let changed = false;
      for (const [k, x] of Object.entries(patch)) {
        if (x !== undefined && v[k] !== x) { v[k] = x; changed = true; }
      }
      if (changed) this.onChange(this.values, true);
      return;
    }
    const until = performance.now() + HOLD_MS;
    const v = this.values as unknown as Record<string, unknown>;
    let changed = false;
    for (const [k, x] of Object.entries(patch)) {
      if (x === undefined) continue;
      this.heldUntil.set(k as RoomVarKey, until);
      if (v[k] !== x) { v[k] = x; changed = true; }
    }
    this.scheduleRelease();
    this.send(patch);
    if (changed) this.onChange(this.values, true);
  }

  /** Back to the defaults (sent like an edit). */
  reset(): void {
    this.set({ ...this.defaults });
  }

  /**
   * Values from the server. `full` (welcome): the room's complete set; keys it does not list are back to
   * the default.
   */
  receive(patch: RoomVarsPatch, full: boolean): void {
    if (full) this.server = {};
    Object.assign(this.server, patch);
    const now = performance.now();
    const v = this.values as unknown as Record<string, unknown>;
    let changed = false;
    for (const k of ROOM_VAR_KEYS) {
      const has = k in patch;
      if (!has && !full) continue;
      if ((this.heldUntil.get(k) ?? 0) > now) continue;
      const x = has ? patch[k] : this.defaults[k];
      if (v[k] !== x) { v[k] = x; changed = true; }
    }
    if (changed) this.onChange(this.values, false);
  }

  private scheduleRelease(): void {
    if (this.holdTimer) return;
    this.holdTimer = window.setTimeout(this.release, HOLD_MS + 20);
  }

  /** Holds that ended: take the server's value where it differs from ours. */
  private release = () => {
    this.holdTimer = 0;
    const now = performance.now();
    const v = this.values as unknown as Record<string, unknown>;
    let changed = false;
    for (const [k, until] of this.heldUntil) {
      if (until > now) continue;
      this.heldUntil.delete(k);
      if (k in this.server && v[k] !== this.server[k]) { v[k] = this.server[k]; changed = true; }
    }
    if (this.heldUntil.size) this.scheduleRelease();
    if (changed) this.onChange(this.values, false);
  };
}
